import { useMemo } from 'react'
import { permissionCheck } from '../../../../../../../../../../baseComponents/PermissionChecker/function'
import { ITTypeQuantityDisplay, ITDiscountDisplay } from '../../../../../../../../../../baseComponents/TwinTable/InputTableLayout/InputTable/Components/Displays'
import { InputTableLayoutStepProps } from '../../../../../../../../../../baseComponents/TwinTable/InputTableLayout/types'
import { CustomRenderBoolean, CustomRenderString } from '../../../../../../../../../../baseComponents/TwinTable/VirtualTableListing/CustomRenders'
import { ColumnOrderTableSchema, OrderData } from '../../../../../../../../../../specificComponents/inputTableLayouts/order/OrderLayout/types'
import OrderSellLayout from '../../../../../../../../../../specificComponents/inputTableLayouts/order/OrderSellLayout'
import { ProductExtendedTyInputTableListing } from '../../../../../../../../../../specificComponents/Product/types'
import { dictionaryComplexFromJsonArr } from '../../../../../../../../../../utils/globals/dictionary'
import useTwinTranslation from '../../../../../../../../../../utils/hooks/useTwinTranslation'
import { getUserPermissions } from '../../../../../../../../../../utils/reducers/getters'
import SellOrderFinishedStepITHeader from './SellOrderFinishedStepITHeader'
import { SellOrderProductModelTypeExtended } from '@teinor/erp/types/company/order/sell/sellOrder/sellOrderProduct'
import useSellOrderHeaderUrls from '../../../../../../../../../../utils/hooks/sells/orders/useSellOrderHeaderUrls'
import useOrderRowRenderer from '../../../../../../../../../../utils/hooks/orders/useOrderRowRenderer'
import { sellOrderStatusInversed } from '@teinor/erp/types/company/order/sell/sellOrder'
import SellOrderFinishedStepHeaderDecider from './SellOrderFinishedStepHeader'

interface SellOrderFinishedStepProps extends InputTableLayoutStepProps { 
}

const SellOrderFinishedStep: React.FC<SellOrderFinishedStepProps> = ({ inputTableURLs, completeData, setCompleteData, getCompleteData, tableConfiguration, setTableConfiguration, userPermissions, headerChanges }) => {
    const { t } = useTwinTranslation()
    const { rowRenderer } = useSellOrderFinishedStepLogic({ completeData })
    const accounts = useMemo(() => dictionaryComplexFromJsonArr(completeData?.header?.OrderAccounts), [completeData?.header?.OrderAccounts])
    const orderHeaderURLs = useSellOrderHeaderUrls()

    if (!completeData) {
        return null
    }
    const statusColumn: ColumnOrderTableSchema<ProductExtendedTyInputTableListing & SellOrderProductModelTypeExtended> = []
    if (completeData?.header.WorkSheetId || completeData?.header?.relatedWorkSheets?.length) {
        statusColumn.push(
            {
                id: 'haveWorkSheetProductStatus',
                dataKey: 'haveWorkSheetProductStatus',
                label: '',
                simpleLabel: t('workSheetStatus', 'Estado hoja de trabajo'),
                component: 'OrderProductWorkSheetStatus',
                hideColumnByDefault: true,
                Order: completeData?.header || {}
            },
        )
    }
    const columns: ColumnOrderTableSchema<ProductExtendedTyInputTableListing & SellOrderProductModelTypeExtended> = [
        {
            id: 'nLine',
            dataKey: 'nLine',
            label: t('line', 'Línea'),
            width: 70,
            hideColumnByDefault: true
        },
        {
            id: 'url',
            dataKey: 'url',
            label: t('image', 'Imagen'),
            component: 'ITDisplayImage',
            width: 70
        },
        {
            id: 'code_bar',
            dataKey: 'code_bar',
            label: t('codeBarShort', 'Cod. de barras'),
            simpleLabel: t('codeBar', 'Código de barras'),
            component: 'OrderSearchProductInputCreate',
            field: 'code_bar',
            typeOnChange: 'onChangeAdd',
            sortable: true,
            hideColumnByDefault: true,
            minWidth: 115
        },
        {
            id: 'reference',
            dataKey: 'reference',
            label: t('reference', 'Referencia'),
            component: 'OrderSearchProductInput',
            field: 'reference',
            sortable: true,
            minWidth: 100,
            hideColumnByDefault: true,
            className: 'quote_order_reference_name'
        },
        {
            id: 'name',
            dataKey: 'name',
            label: t('name', 'Nombre'),
            component: 'OrderSearchProductInputName',
            field: 'name',
            sortable: true,
            hideColumnByDefault: true,
            className: 'quote_order_reference_name'
        },
        {
            id: 'referenceAndName',
            dataKey: 'name',
            label: t('referenceName', 'Referencia y nombre'),
            component: 'OrderSearchProductInputReferenceAndName',
            field: 'referenceAndName',
            sortable: true,
            minWidth: 180,
            className: 'quote_order_reference_name'
        },
        {
            id: 'editProduct',
            dataKey: 'ProductId',
            label: '',
            simpleLabel: t('buttonSeeProduct', 'Botón de ver producto'),
            component: 'OrderEditProduct',
            width: 35,
            havePermission: permissionCheck(getUserPermissions('product.profile.main'), 'read'),
        },
        {
            id: 'descriptionDocuments',
            dataKey: 'descriptionDocuments',
            label: t('description', 'Descripción'),
            customRender: (parameterValue) => <CustomRenderString value={parameterValue} />,
            hideColumnByDefault: true
        },
        {
            id: 'shortDescriptionDocuments',
            dataKey: 'shortDescriptionDocuments',
            label: t('shortDescriptionS', 'Desc. corta'),
            simpleLabel: t('shortDescription', 'Descripción corta'),
            customRender: (parameterValue) => <CustomRenderString value={parameterValue} />,
            hideColumnByDefault: true
        },
        {
            id: 'notes',
            dataKey: 'notes',
            label: t('notes', 'Notas'),
            customRender: (parameterValue, allRowData) => <CustomRenderString value={parameterValue} />
        },
        {
            id: 'SpecificPriceRuleId',
            dataKey: 'SpecificPriceRuleId',
            label: t('priceRule', 'Tarifa'),
            customRender: (parameterValue, allRowData) => <CustomRenderString value={'T' + allRowData?.['SpecificPriceRule.id'] + ' - ' + allRowData?.['SpecificPriceRule.name']} />,
            hideColumnByDefault: true
        },
        {
            id: 'initialUnitPriceWithoutTax',
            dataKey: 'initialUnitPriceWithoutTax',
            label: () => <div className='text-right mr-15 w-full'>{t('price', 'Precio')}</div>,
            simpleLabel: t('price', 'Precio'),
            component: 'ITDisplayPrice',
            width: 100,
        },
        {
            id: 'manualPrice',
            dataKey: 'manualPrice',
            label: t('manualPriceS', 'P. man.'),
            simpleLabel: t('manualPrice', 'Precio manual'),
            toolTip: t('manualPrice', 'Precio manual'),
            customRender: (parameterValue) => <CustomRenderBoolean value={String(parameterValue) === 'true'} width={60} />,
            hideColumnByDefault: true,
            width: 85,
        },
        {
            id: 'quantity',
            dataKey: 'quantity',
            label: t('quantity', 'Cantidad'),
            width: 110,
            customRender: (parameterValue, allRowData) => <ITTypeQuantityDisplay measure={allRowData.typeQuantity} value={parameterValue} />
        },
        {
            id: 'desiredQuantity',
            dataKey: 'desiredQuantity',
            label: t('desiredQuantityShort', 'Cant. des.'),
            simpleLabel: t('desiredQuantity', 'Cantidad deseada'),
            toolTip: t('desiredQuantity', 'Cantidad deseada'),
            width: 105,
            customRender: (parameterValue, allRowData) => <ITTypeQuantityDisplay measure={allRowData.typeQuantity} value={parameterValue} />
        },
        {
            id: 'quantity2',
            dataKey: 'quantity2',
            label: t('quantitySecondRes', 'Cant. sec.'),
            simpleLabel: t('quantitySecondary', 'Cantidad secundaria'),
            width: 90,
            customRender: (parameterValue, allRowData) => <ITTypeQuantityDisplay measure={allRowData.typeQuantity2} value={parameterValue} />,
            hideColumnByDefault: true
        },
        {
            id: 'OrderAccountId',
            dataKey: 'OrderAccountId',
            label: t('chapter', 'Capítulo'),
            customRender: (parameterValue) => <CustomRenderString value={accounts[parameterValue]?.name || ''} />,
            hideColumnByDefault: true
        },
        {
            id: 'discount',
            dataKey: 'discount',
            label: t('discount', 'Descuento'),
            width: 90,
            customRender: (parameterValue, allRowData) => <ITDiscountDisplay CurrencyId={completeData?.header.CurrencyId} value={parameterValue} typeDiscount={allRowData.typeDiscount} />
        },
        {
            id: 'rowPriceWithoutTaxPrev',
            dataKey: 'rowPriceWithoutTaxPrev',
            label: () => <div className='text-right mr-15 w-full'>{t('rowPrice', 'Precio Línea')}</div>,
            simpleLabel: t('rowPrice', 'Precio Línea'),
            component: 'ITDisplayFinalPrice',
            with_vat: false,
            width: 120,
            hideColumnByDefault: true
        },
        {
            id: 'rowPriceWithoutTaxAndTotalDiscount',
            dataKey: 'rowPriceWithoutTaxAndTotalDiscount',
            label: () => <div className='text-right w-full'>{t('priceTotal', 'Precio Total')}</div>,
            simpleLabel: t('priceTotal', 'Precio Total'),
            component: 'ITDisplayFinalPrice',
            with_vat: false,
            width: 120,
        },
        {
            id: 'rowPriceWithTaxAndTotalDiscount',
            dataKey: 'rowPriceWithTaxAndTotalDiscount',
            label: () => <div className='text-right w-full'>{t('totalPriceWithTaxShort', 'Precio Total Imp.')}</div>,
            simpleLabel: t('totalPriceWithTaxShort', 'Precio Total Imp.'),
            component: 'ITDisplayFinalPrice',
            with_vat: false,
            width: 130,
            hideColumnByDefault: true,
        },
        {
            id: 'totalWithTaxWithComplements',
            dataKey: 'totalWithTaxWithComplements',
            simpleLabel: t('totalWithComplementsAndVat', 'Total con complementos e imp.'),
            label: () => <div className='text-right w-full' >{t('withComplementsAndVatS', 'Con comp. e imp.')}</div>,
            component: 'ITDisplayPrice',
            width: 160,
            hideColumnByDefault: true,
            toolTip: t('totalWithComplementsAndVatL', 'Total con complementos e impuestos'),
        },
        {
            id: 'totalWithoutTaxWithComplements',
            dataKey: 'totalWithoutTaxWithComplements',
            simpleLabel: t('totalWithComplements', 'Total con complementos'),
            label: () => <div className='text-right w-full'> {t('withComplementsS', 'Con comp.')}</div>,
            component: 'ITDisplayPrice',
            width: 110,
            hideColumnByDefault: true,
            toolTip: t('totalWithComplements', 'Total con complementos')
        },
        {
            id: 'totalWithoutTaxWithComplementsPrev',
            dataKey: 'totalWithoutTaxWithComplementsPrev',
            simpleLabel: t('totalWithoutTaxWithComplementsPrev', 'Total con complementos antes de desc.'),
            label: () => <div className='text-right w-full'> {t('withComplementsS', 'Con comp.')}</div>,
            component: 'ITDisplayPrice',
            width: 110,
            hideColumnByDefault: true,
            toolTip: t('totalWithoutTaxWithComplementsPrev', 'Total con complementos antes de descuentos'),
        },
        {
            id: 'unitPriceWithComplements',
            dataKey: 'unitPriceWithComplements',
            simpleLabel: t('unitPriceWithComplements', 'Precio unitario con complementos'),
            label: () => <div className='text-right w-full'> {t('unitPricewithComplementsS', 'P. U. comp.')}</div>,
            component: 'ITDisplayPrice',
            width: 110,
            hideColumnByDefault: true,
            toolTip: t('unitPriceWithComplements', 'Precio unitario con complementos'),
        },
        {
            id: 'processed',
            dataKey: 'processed',
            label: t('status', 'Estado'),
            component: 'OrderSaveRemainingProduct',
            width: 110,
        },
        {
            id: 'unique_number',
            dataKey: 'unique_number',
            label: t('uniqueNumberShort', 'Nº único'),
            simpleLabel: t('uniqueNumber', 'Número único'),
            toolTip: t('uniqueNumber', 'Número único'),
            component: 'OrderUniqueNumberProductReadOnly',
            hideColumnByDefault: true,
        },
        {
            id: 'PrintTags',
            dataKey: 'ProductId',
            label: '',
            simpleLabel: t('printTags', 'Imprimir etiquetas'),
            width: 33,
            component: 'OrderPrintTags',
            hideColumnByDefault: true
        },
        ...statusColumn
    ]
    return (
        <OrderSellLayout modelId='SellOrderId' name='SellOrderFinishedStep' translations={{ headerTitle: t('sellOrder', 'Pedido') }} buttonSave={
            <SellOrderFinishedStepHeaderDecider completeData={completeData} onSubmit={getCompleteData} data={completeData as OrderData} isReadOnly={true} />
        } InputTableHeaderChildren={SellOrderFinishedStepITHeader} isReadOnly={true} columns={columns} {...{ orderHeaderURLs, rowRenderer, inputTableURLs, completeData, setCompleteData, getCompleteData, tableConfiguration, setTableConfiguration, userPermissions, headerChanges, orderStatusDict: sellOrderStatusInversed}} />
    )
}

interface SellOrderFinishedStepLogicProps {
    completeData: OrderData | null
}

export const useSellOrderFinishedStepLogic = ({ completeData }: SellOrderFinishedStepLogicProps) => {
    const rowRenderer = useOrderRowRenderer({ completeData, isFinishedStep: true})

    return { rowRenderer }
}



export default SellOrderFinishedStep